// @ts-nocheck
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_URL, authFetch, fetchTeacherSubjectsWithStudents, fetchAdminSubjectsWithStudents, fetchEvidenceFile, fetchBreakoutRooms } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import BackToTop from '../../components/BackToTop';
import PageBackNavigation from '../../components/PageBackNavigation';
import StudentNameOrderToggle from '../../components/StudentNameOrderToggle';
import { sortStudentsByName, useStudentNameOrder } from '../../utils/studentSort';
import { normalizeLiteracyWritingStrand } from '../../utils/progressStatus';
import './StandardGradeEntry.css';
import { COLOR_SCORE_PRESETS, GRADING_COLORS, COLOR_NAMES, GRADING_STYLES, GRADING_PERCENTAGE_RANGES, colorFromScore } from '../../utils/grading';
import { ADDITIONAL_SUPPORT_LABEL } from '../../components/AdditionalSupportIndicator';
import DataEntryDraftList from '../../components/DataEntryDraftList';
import { publishDataEntryDraft, saveDataEntryDraft } from '../../services/dataEntryDrafts';
import FractionScoreInput from '../../components/FractionScoreInput';
import { fractionScore } from '../../utils/fractionScore';
import { SUBMISSION_STATUSES } from '../../utils/successSkillsSubmission';
import { timelinessStandard } from '../../utils/taskTimeliness';

const unique = (values) => Array.from(new Set(values.filter(Boolean)));

function PreviewNotes({ studentLabel, notes, onChange }: any) {
  const inputRef = useRef<any>(null);

  useLayoutEffect(() => {
    const input = inputRef.current;
    const resize = () => {
      input.style.height = 'auto';
      input.style.height = `${input.scrollHeight + 3}px`;
    };
    resize();
    let width = input.clientWidth;
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => {
      if (input.clientWidth !== width) {
        width = input.clientWidth;
        resize();
      }
    }) : null;
    observer?.observe(input);
    window.addEventListener('resize', resize);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', resize);
    };
  }, [notes]);

  return (
    <label className="sge-preview-notes">
      <span>Notes</span>
      <textarea ref={inputRef} className="sge-preview-notes-edit"
        aria-label={`${studentLabel} notes`} value={notes} rows={2}
        onChange={onChange} placeholder="Notes for this student" />
    </label>
  );
}

// A color preset alone shouldn't show a percentage in the score field, but the
// preset value still needs to be saved; fall back to it only when no number was typed.
function resolveEffectiveScore(scoreValue, explicitColor) {
  if (scoreValue !== '' && scoreValue !== null && scoreValue !== undefined) return scoreValue;
  if (explicitColor && COLOR_SCORE_PRESETS[explicitColor] !== undefined) return String(COLOR_SCORE_PRESETS[explicitColor]);
  return '';
}

const STRAND_DESCRIPTION_BY_SUBJECT = {
  Mathematics: {
    NS: 'Number Sense',
    NBT: 'Number Sense and Operations in Base Ten',
    NF: 'Number Sense and Operations in Fractions',
    RA: 'Relationships and Algebraic Thinking',
    RP: 'Ratios and Proportional Relationships',
    EEI: 'Expressions, Equations and Inequalities',
    DSP: 'Data Analysis, Statistics and Probability',
    DS: 'Data and Statistics',
    GM: 'Geometry and Measurement',
    F: 'Functions',
  },
  Literacy: {
    RF: 'Reading Foundations',
    RL: 'Reading Literary Text',
    RI: 'Reading Informational Text',
    W: 'Writing',
    SL: 'Speaking and Listening',
    L: 'Language',
  },
};

function getScoreColor(scoreValue, explicitColor) {
  if (GRADING_COLORS.includes(explicitColor)) {
    return explicitColor;
  }

  return colorFromScore(scoreValue) || '';
}

function getStrandDescriptionFromCode(subject, strandCode) {
  const subjectKey = String(subject || '').trim();
  const codeKey = String(strandCode || '').trim().toUpperCase();
  if (!subjectKey || !codeKey) return '';
  return STRAND_DESCRIPTION_BY_SUBJECT[subjectKey]?.[codeKey] || '';
}

function getTodayDateInputValue() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function StandardGradeEntry({ embeddedSelections, fixedStudent, initialDraft, onPublished, onBusyChange, onDraftSaved }: any) {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const showPercentageRanges = location.pathname.startsWith('/teacher');
  const progressionPath = location.pathname.startsWith('/admin') ? '/admin/standards-progression' : '/teacher/standards-progression';
  const embedded = Boolean(fixedStudent && embeddedSelections);
  const lockedStudent = embedded ? fixedStudent : location.state?.profileStudent;
  const lockedStudentId = lockedStudent ? Number(lockedStudent.student_id) : null;
  const studentLocked = Boolean(lockedStudent);
  const initialForm = initialDraft?.form;
  const [draftId, setDraftId] = useState(initialDraft?.id ?? null);
  const [restoredSelections, setRestoredSelections] = useState(initialForm?.selections || null);
  const [draftRevision, setDraftRevision] = useState<any>(0);
  const [savingDraft, setSavingDraft] = useState<any>(false);
  const group = embedded || restoredSelections ? null : location.state?.group || null;
  const incomingClassId = !embedded && !restoredSelections && location.state?.classId ? String(location.state.classId) : '';
  const [nameOrder] = useStudentNameOrder();
  const selectionList = useMemo(() => {
    if (restoredSelections) return restoredSelections;
    if (embeddedSelections) return embeddedSelections;
    if (Array.isArray(location.state?.selections) && location.state.selections.length > 0) {
      return location.state.selections;
    }
    if (location.state?.selection) {
      return [location.state.selection];
    }
    return [];
  }, [location.state, embeddedSelections, restoredSelections]);
  const [uploading, setUploading] = useState<any>(false);
  const [viewingEvidence, setViewingEvidence] = useState<any>(false);
  const [evidenceFilename, setEvidenceFilename] = useState(initialForm?.evidenceFilename || '');
  const [showPreview, setShowPreview] = useState<any>(false);
  const [selectedStudents, setSelectedStudents] = useState(() => studentLocked ? [lockedStudentId] : initialForm?.selectedStudents || []);
  const [studentDrafts, setStudentDrafts] = useState(initialForm?.studentDrafts || {});
  const [studentSearchTerm, setStudentSearchTerm] = useState<any>('');
  const [saving, setSaving] = useState<any>(false);
  const [loading, setLoading] = useState<any>(true);
  const [error, setError] = useState<any>(null);
  const [message, setMessage] = useState<any>(null);
  const [subjects, setSubjects] = useState<any>([]);
  const [classId, setClassId] = useState(initialForm?.classId || incomingClassId);
  const [classGroups, setClassGroups] = useState<any>([]);
  const [selectedGroupId, setSelectedGroupId] = useState(group?.breakout_room_id ? String(group.breakout_room_id) : '');
  const [loadingGroups, setLoadingGroups] = useState<any>(false);
  const [title, setTitle] = useState(selectionList[0]?.code || '');
  const [dataType, setDataType] = useState(initialForm?.dataType || 'assessment');
  const [assessmentType, setAssessmentType] = useState(initialForm?.assessmentType || 'formative');
  const [dateValue, setDateValue] = useState(initialForm?.dateValue ?? getTodayDateInputValue());
  const [titleOfDataPoint, setTitleOfDataPoint] = useState(initialForm?.titleOfDataPoint || '');
  const [submissionStatus, setSubmissionStatus] = useState(initialForm?.submissionStatus || 'on_time');
  const [score, setScore] = useState(initialForm?.score || '');
  const [fraction, setFraction] = useState(initialForm?.fraction || '');
  const [selectedScoreColor, setSelectedScoreColor] = useState(() => initialForm?.selectedScoreColor || (embedded ? '' : location.state?.initialScoreColor || ''));
  const sharedFraction = fractionScore(fraction);
  const displayedScore = fraction.trim() ? sharedFraction.score : score;
  const displayedScoreColor = fraction.trim() ? colorFromScore(sharedFraction.score) || '' : selectedScoreColor;
  const [notes, setNotes] = useState(initialForm?.notes || '');
  const [visibility, setVisibility] = useState(initialForm?.visibility || 'private');
  const [accommodationsApplied, setAccommodationsApplied] = useState(initialForm?.accommodationsApplied || false);
  const [returnTo, setReturnTo] = useState<any>(null);
  const [returnLabel, setReturnLabel] = useState('← Back');
  const [progressionState, setProgressionState] = useState<any>(null);
  const busy = saving || savingDraft || uploading;
  useEffect(() => {
    onBusyChange?.(busy);
  }, [busy, onBusyChange]);

  // Prefill from navigation state if available
  useEffect(() => {
    if (!embedded && location.state) {
      if (location.state.returnTo) setReturnTo(location.state.returnTo);
      if (location.state.returnTo?.label) setReturnLabel(location.state.returnTo.label);
      if (location.state.progressionState) setProgressionState(location.state.progressionState);
    }
  }, [location.state, embedded]);

  useEffect(() => {
    if (selectionList[0]?.code) {
      setTitle(selectionList[0].code);
    }
  }, [selectionList]);

  useEffect(() => {
    // Prevent browser scroll restoration from landing users near the bottom.
    if (!embedded) window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [classId, selectionList, embedded]);

  const handleBack = () => {
    if (returnTo && typeof returnTo === 'object' && returnTo.pathname) {
      navigate(returnTo.pathname, returnTo.state ? { state: returnTo.state } : undefined);
      return;
    }

    if (typeof returnTo === 'string' && returnTo) {
      navigate(returnTo);
      return;
    }

    navigate(progressionPath, { state: { progressionState } });
  };

  const subjectName = selectionList[0]?.subject || progressionState?.subject || '';
  const submissionSelectionEligible = dataType === 'assignment' && selectionList.length > 0
    && selectionList.every(selection =>
      String(selection.subject || subjectName).trim().toLowerCase() === 'success skills');
  const strandCodeFallback = selectionList[0]?.strand || progressionState?.strand || '';
  const strandDisplay =
    normalizeLiteracyWritingStrand(
      subjectName,
      selectionList[0]?.grade || progressionState?.grade,
      selectionList[0]?.code,
      selectionList[0]?.strandDescription || progressionState?.strandDescription || '',
    ) ||
    selectionList[0]?.strandDescription ||
    progressionState?.strandDescription ||
    getStrandDescriptionFromCode(subjectName, strandCodeFallback) ||
    selectionList[0]?.strand ||
    progressionState?.strand ||
    '';
  const breadcrumb = [
    subjectName,
    selectionList[0]?.domain || progressionState?.domain || '',
    strandDisplay,
  ].filter(Boolean).join(' → ');

  const selectedStandardTitle = useMemo(() => {
    if (!selectionList.length) return '';

    const codes = unique(selectionList.map((selection) => selection.code?.trim()).filter(Boolean));
    if (codes.length > 1) {
      return codes.join(', ');
    }

    return codes[0] || '';
  }, [selectionList]);

  useEffect(() => {
    if (selectedStandardTitle) {
      setTitle(selectedStandardTitle);
    }
  }, [selectedStandardTitle]);

  const standardCodes = useMemo(() => {
    const fromSelection = selectionList.map((selection) => selection.code).filter(Boolean);
    if (fromSelection.length > 0) {
      return fromSelection;
    }
    return title.trim() ? [title.trim()] : [];
  }, [selectionList, title]);

  const entryHeading = unique(standardCodes).length > 1
    ? 'Data Points for Multiple Standards'
    : breadcrumb;
  const compactPreviewColors = selectedStudents.length > 1 || standardCodes.length > 1;

  const selectedStandardsDetails = useMemo(() => {
    if (!selectionList.length) {
      return [];
    }

    return selectionList.map((selection, index) => ({
      key: `${selection.code || 'standard'}-${index}`,
      code: selection.code || `Standard ${index + 1}`,
      description: selection.standardDescription || selection.text || selection.clusterExpectationDescription || '',
    }));
  }, [selectionList]);

  const isAdminRole = user?.role === 'admin' || user?.role === 'school_admin' || user?.role === 'district_admin' || (Array.isArray(user?.roles) && user.roles.some(r => ['admin', 'school_admin', 'district_admin'].includes(r))) || location.pathname.startsWith('/admin');
  const userId = user?.user_id ?? user?.id;
  const schoolId = user?.school_id;

  // Reload the roster only when the account or access scope changes.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const fetchFn = isAdminRole ? fetchAdminSubjectsWithStudents : fetchTeacherSubjectsWithStudents;
    fetchFn()
      .then((data) => { if (!cancelled) setSubjects(data?.subjects || []); })
      .catch(() => { if (!cancelled) setError('Could not load classes and students.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [userId, schoolId, isAdminRole]);

  const classOptions = useMemo(() => {
    const allClasses = subjects.flatMap((subject) => subject.classes || [])
      .filter(cls => !studentLocked || (cls.students || []).some(student => Number(student.student_id) === lockedStudentId));
    if (restoredSelections) return allClasses;
    if (incomingClassId) {
      const matched = allClasses.filter((cls) => String(cls.class_id) === incomingClassId);
      if (matched.length > 0) return matched;
    }
    if (subjectName) {
      const subjectMatch = subjects.find((subject) => subject.subject_name === subjectName);
      const subjectClasses = subjectMatch?.classes?.filter(cls => allClasses.includes(cls));
      if (subjectClasses?.length) return subjectClasses;
    }
    return allClasses;
  }, [subjects, incomingClassId, subjectName, restoredSelections, studentLocked, lockedStudentId]);

  useEffect(() => {
    if (!classOptions.length) return;
    if (classOptions.some((cls) => String(cls.class_id) === String(classId))) return;
    setClassId(String(classOptions[0].class_id));
  }, [classOptions, classId]);

  const selectedClass = useMemo(
    () => classOptions.find((cls) => String(cls.class_id) === String(classId)),
    [classOptions, classId]
  );

  useEffect(() => {
    if (studentLocked) return;
    if (!classId) {
      setClassGroups([]);
      setSelectedGroupId('');
      return;
    }

    let cancelled = false;
    setLoadingGroups(true);
    fetchBreakoutRooms(classId)
      .then((groups) => {
        if (cancelled) return;
        const normalizedGroups = Array.isArray(groups) ? groups : [];
        setClassGroups(normalizedGroups);
        if (group?.breakout_room_id && normalizedGroups.some((entry) => String(entry.breakout_room_id) === String(group.breakout_room_id))) {
          setSelectedGroupId(String(group.breakout_room_id));
        } else {
          setSelectedGroupId((previous) => normalizedGroups.some((entry) => String(entry.breakout_room_id) === previous) ? previous : '');
        }
      })
      .catch(() => {
        if (!cancelled) {
          setClassGroups([]);
          setSelectedGroupId('');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingGroups(false);
      });

    return () => {
      cancelled = true;
    };
  }, [classId, group?.breakout_room_id, studentLocked]);

  const selectedGroup = useMemo(
    () => classGroups.find((entry) => String(entry.breakout_room_id) === String(selectedGroupId)) || null,
    [classGroups, selectedGroupId]
  );

  const students = useMemo(() => {
    if (embedded) {
      const student = subjects.flatMap(subject => subject.classes || [])
        .flatMap(cls => cls.students || [])
        .find(student => String(student.student_id) === String(fixedStudent.student_id));
      return student ? [{ ...student, student_id: Number(student.student_id), full_name: fixedStudent.full_name }] : [];
    }
    if (studentLocked) {
      const student = (selectedClass?.students || [])
        .find(student => Number(student.student_id) === lockedStudentId);
      return student ? [{ ...student, student_id: lockedStudentId }] : [];
    }
    const sourceStudents = selectedClass?.students || [];
    const seen = new Set();
    const uniqueStudents = sourceStudents.filter((student) => {
      if (!student?.student_id || seen.has(student.student_id)) {
        return false;
      }
      seen.add(student.student_id);
      return true;
    });

    const activeGroup = selectedGroup || group;
    const scoped = !activeGroup?.students?.length
      ? uniqueStudents
      : uniqueStudents.filter((student) => new Set(activeGroup.students.map((s) => s.student_id)).has(student.student_id));
    return sortStudentsByName(scoped, nameOrder);
  }, [selectedClass, selectedGroup, group, nameOrder, embedded, fixedStudent, subjects, studentLocked, lockedStudentId]);

  const filteredStudents = useMemo(() => {
    const term = studentSearchTerm.trim().toLowerCase();
    if (!term) return students;
    return students.filter((student) => {
      const label = (student.full_name || `${student.first_name || ''} ${student.last_name || ''}`.trim() || `Student ${student.student_id}`).toLowerCase();
      return label.includes(term);
    });
  }, [students, studentSearchTerm]);

  useEffect(() => {
    if (studentLocked || !group?.students?.length) return;
    const allowedStudentIds = new Set(students.map((student) => student.student_id));
    const defaultStudentIds = group.students
      .map((student) => student.student_id)
      .filter((studentId) => allowedStudentIds.has(studentId));
    setSelectedStudents(defaultStudentIds);
  }, [group, students, studentLocked]);

  // Auto-select a pre-specified student (e.g. when arriving from a student profile tile click)
  const preselectedStudentId = !embedded && !restoredSelections && location.state?.preselectedStudentId
    ? Number(location.state.preselectedStudentId)
    : null;

  useEffect(() => {
    if (studentLocked) {
      setSelectedStudents(previous => previous.length === 1 && previous[0] === lockedStudentId ? previous : [lockedStudentId]);
      return;
    }
    if (!preselectedStudentId || !students.length) return;
    if (students.some((s) => s.student_id === preselectedStudentId)) {
      setSelectedStudents((prev) =>
        prev.includes(preselectedStudentId) ? prev : [preselectedStudentId]
      );
    }
  }, [students, preselectedStudentId, studentLocked, lockedStudentId]);

  const allStudentsSelected = filteredStudents.length > 0
    && filteredStudents.every((student) => selectedStudents.includes(student.student_id));

  const toggleAllStudents = () => {
    if (allStudentsSelected) {
      const visibleIds = new Set(filteredStudents.map((student) => student.student_id));
      setSelectedStudents((prev) => prev.filter((id) => !visibleIds.has(id)));
      setStudentDrafts((prev) => {
        const next = { ...prev };
        visibleIds.forEach((id) => delete next[String(id)]);
        return next;
      });
      return;
    }
    const visibleIds = filteredStudents.map((student) => student.student_id);
    setSelectedStudents((prev) => Array.from(new Set([...prev, ...visibleIds])));
  };

  const updateStudentDraft = (studentId, updates) => {
    setStudentDrafts((prev) => ({
      ...prev,
      [String(studentId)]: {
        ...(prev[String(studentId)] || {}),
        ...updates,
      },
    }));
  };

  const updateCellDraft = (studentId, standardCode, updates) => {
    setStudentDrafts((prev) => {
      const studentDraft = prev[String(studentId)] || {};
      const byStandard = studentDraft.byStandard || {};
      return {
        ...prev,
        [String(studentId)]: {
          ...studentDraft,
          byStandard: {
            ...byStandard,
            [standardCode]: { ...(byStandard[standardCode] || {}), ...updates },
          },
        },
      };
    });
  };

  // One row per selected student, one cell per standard; cells fall back to the shared score/color.
  const previewRows = useMemo(() => {
    return selectedStudents.map((studentId) => {
      const student = students.find((entry) => entry.student_id === studentId);
      const draft = studentDrafts[String(studentId)] || {};
      const timingCode = timelinessStandard(student?.grade_level || (embedded ? fixedStudent?.grade_level : null));
      const matchingSubmissionCodes = submissionSelectionEligible && timingCode
        ? standardCodes.filter(code => code.trim().replace(/\.+$/, '') === timingCode.replace(/\.+$/, ''))
        : [];
      const cells = standardCodes.map((standardCode) => {
        const timingStatus = matchingSubmissionCodes.includes(standardCode)
          ? SUBMISSION_STATUSES.find(status => status.id === (draft.submissionStatus || submissionStatus))
          : null;
        const cellDraft = (draft.byStandard || {})[standardCode] || {};
        const hasOverride = cellDraft.score !== undefined || cellDraft.selectedScoreColor !== undefined || cellDraft.fraction !== undefined;
        const cellFraction = hasOverride ? cellDraft.fraction || '' : fraction;
        const calculated = fractionScore(cellFraction);
        const cellScore = cellFraction.trim() ? calculated.score : cellDraft.score !== undefined ? cellDraft.score : score;
        const cellSelectedColor = cellFraction.trim() ? colorFromScore(calculated.score) || ''
          : cellDraft.selectedScoreColor !== undefined ? cellDraft.selectedScoreColor : selectedScoreColor;
        return {
          standardCode,
          standardDescription: selectedStandardsDetails.find(standard => standard.code === standardCode)?.description.trim() || '',
          score: timingStatus ? timingStatus.defaultScore : cellScore,
          fraction: timingStatus ? '' : cellFraction,
          fractionError: timingStatus ? null : calculated.error,
          selectedScoreColor: timingStatus ? timingStatus.defaultColor : cellSelectedColor,
          color: timingStatus ? timingStatus.defaultColor : getScoreColor(cellScore, cellSelectedColor),
          submissionScored: Boolean(timingStatus),
        };
      });
      return {
        studentId,
        submissionCodes: matchingSubmissionCodes,
        studentLabel: student ? (student.full_name || `${student.first_name || ''} ${student.last_name || ''}`.trim() || `Student ${student.student_id}`) : `Student ${studentId}`,
        notes: draft.notes !== undefined ? draft.notes : notes,
        accommodationsApplied: draft.accommodationsApplied ?? accommodationsApplied,
        submissionStatus: draft.submissionStatus || submissionStatus,
        cells,
      };
    });
  }, [selectedStudents, students, standardCodes, selectedStandardsDetails, score, fraction, notes, selectedScoreColor, studentDrafts, accommodationsApplied, submissionStatus, embedded, fixedStudent, submissionSelectionEligible]);

  const submissionStatusEnabled = previewRows.some(row => row.submissionCodes.length > 0);

  // Student selection toggle
  const toggleStudent = (studentId) => {
    setSelectedStudents((prev) => {
      if (prev.includes(studentId)) {
        setStudentDrafts((drafts) => {
          const next = { ...drafts };
          delete next[String(studentId)];
          return next;
        });
        return prev.filter((id) => id !== studentId);
      }
      return [...prev, studentId];
    });
  };

  const handleEvidenceUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await authFetch(`${API_URL}/api/data-points/evidence/upload`, {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Failed to upload evidence file (${res.status}).`);
      }
      const data = await res.json();
      setEvidenceFilename(data.filename || '');
    } catch (uploadError: any) {
      const msg = String(uploadError?.message || 'Failed to upload evidence file.');
      setError(msg === 'Failed to fetch' ? 'Upload failed: network/API connection issue. Please retry and check backend availability.' : msg);
    } finally {
      setUploading(false);
    }
  };

  const handleViewEvidence = async () => {
    if (!evidenceFilename) return;
    setViewingEvidence(true);
    setError(null);
    try {
      const blob = await fetchEvidenceFile(evidenceFilename);
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
    } catch (viewError: any) {
      setError(viewError.message || 'Could not open the attached evidence file.');
    } finally {
      setViewingEvidence(false);
    }
  };

  const validEntryTargets = () => {
    if (selectedStudents.some(id => !students.some(student => student.student_id === id))
      || (studentLocked && (selectedStudents.length !== 1 || selectedStudents[0] !== lockedStudentId))) {
      setError('A selected student is not in your accessible class roster. Review the student selection before saving or publishing.');
      return false;
    }
    if (!selectedStudents.length) {
      setError('Select at least one student.');
      return false;
    }
    if (!standardCodes.length) {
      setError('Choose or enter a standard code before saving or publishing.');
      return false;
    }
    return true;
  };

  const resumeDraft = draft => {
    if (studentLocked && (draft.form.selectedStudents.length !== 1 || draft.form.selectedStudents[0] !== lockedStudentId)) {
      setError('This entry is limited to the student whose profile you opened. Open class data entry to resume a draft for other students.');
      return;
    }
    if ((selectedStudents.length || titleOfDataPoint || notes || score || fraction || selectedScoreColor
      || evidenceFilename || Object.keys(studentDrafts).length)
      && !window.confirm('Replace your current entries with this saved draft? Save Draft first if you want to keep your current work.')) {
      return;
    }
    const form = draft.form;
    setDraftId(draft.id);
    setRestoredSelections(form.selections);
    setClassId(form.classId);
    setSelectedGroupId('');
    setSelectedStudents(form.selectedStudents);
    setStudentDrafts(form.studentDrafts);
    setTitleOfDataPoint(form.titleOfDataPoint);
    setDataType(form.dataType);
    setAssessmentType(form.assessmentType);
    if (form.submissionStatus) {
      setSubmissionStatus(form.submissionStatus);
    }
    setDateValue(form.dateValue);
    setScore(form.score);
    setFraction(form.fraction || '');
    setSelectedScoreColor(form.selectedScoreColor);
    setNotes(form.notes);
    setVisibility(form.visibility);
    setAccommodationsApplied(form.accommodationsApplied);
    setEvidenceFilename(form.evidenceFilename);
    setShowPreview(true);
    setError(null);
    setMessage('Draft resumed. Review it before publishing.');
  };

  const handleSaveDraft = async () => {
    if (!validEntryTargets()) return;
    if (!schoolId) {
      setError('Select a school before saving a draft.');
      return;
    }
    setSavingDraft(true);
    setError(null);
    setMessage(null);
    try {
      const draft = await saveDataEntryDraft(draftId, schoolId, {
        selections: selectionList.length ? selectionList.map((selection, index) => ({
          code: selection.code,
          subject: String(selection.subject || ''),
          grade: String(selection.grade ?? ''),
          domain: String(selection.domain || ''),
          strand: String(selection.strand ?? ''),
          strandDescription: selection.strandDescription || '',
          standardDescription: selectedStandardsDetails[index].description,
        })) : standardCodes.map(code => ({ code })),
        selectedStudents, studentDrafts, classId, titleOfDataPoint, dataType, assessmentType,
        submissionStatus,
        dateValue, score, fraction, selectedScoreColor, notes, visibility, accommodationsApplied, evidenceFilename,
      });
      setDraftId(draft.id);
      setDraftRevision(value => value + 1);
      setMessage('Draft saved to your account. You can close this form and resume it later. Nothing has been published.');
      onDraftSaved?.();
    } catch (draftError: any) {
      setError(draftError.message || 'Could not save the draft. Your entries are still in this form.');
    } finally {
      setSavingDraft(false);
    }
  };

  const handleSave = async () => {
    if (!validEntryTargets()) return;
    const invalidRow = previewRows.find(row => row.cells.some(cell => cell.fractionError));
    if (invalidRow) {
      setError(`Fix the invalid fraction for ${invalidRow.studentLabel} before publishing.`);
      setShowPreview(true);
      return;
    }
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const payloads = previewRows.flatMap((row) => {
        const studentPayloads = row.cells.map((cell) => {
          const statusConfig = row.submissionCodes.includes(cell.standardCode)
            ? SUBMISSION_STATUSES.find(status => status.id === row.submissionStatus)
            : null;
          const effectiveScore = resolveEffectiveScore(cell.score, cell.selectedScoreColor);
          // An empty score must save as null, not Number('') === 0.
          const numericScore = String(effectiveScore).trim() === '' ? NaN : Number(effectiveScore);
          const color = getScoreColor(cell.score, cell.selectedScoreColor) || null;

          return {
            standard_code: cell.standardCode,
            student_id: row.studentId,
            title: titleOfDataPoint.trim() || null,
            data_type: dataType,
            accommodations_applied: row.accommodationsApplied,
            score: statusConfig ? statusConfig.defaultScore : Number.isFinite(numericScore) ? Math.round(numericScore) : null,
            notes: statusConfig ? `Submission: ${statusConfig.label}${row.notes ? ` | ${row.notes}` : ''}` : row.notes || null,
            notes_visibility: visibility,
            date: dateValue || null,
            evidence: evidenceFilename || null,
            color: statusConfig ? statusConfig.defaultColor : color,
          };
        });

        return studentPayloads;
      });

      if (draftId != null) {
        await publishDataEntryDraft(draftId, payloads);
      } else {
        const responses = await Promise.all(payloads.map((payload) =>
          authFetch(`${API_URL}/api/data-points/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          })
        ));

        const failed = responses.find((response) => !response.ok);
        if (failed) {
          const errorBody = await failed.json().catch(() => ({}));
          throw new Error(errorBody.detail || 'Failed to publish data point(s).');
        }
      }

      setDraftId(null);
      setMessage('Data point(s) published!');
      setShowPreview(false);
      setStudentDrafts({});
      if (embedded) {
        onPublished?.();
        return;
      }
      const publishedProgressionState = {
        ...(progressionState || {}),
        publishedStandardCode: standardCodes[0],
      };
      if (returnTo && typeof returnTo === 'object' && returnTo.pathname) {
        navigate(returnTo.pathname, {
          state: {
            ...(returnTo.state || {}),
            progressionState: {
              ...(returnTo.state?.progressionState || publishedProgressionState),
              publishedStandardCode: standardCodes[0],
            },
          },
        });
      } else {
        navigate(progressionPath, { state: { progressionState: publishedProgressionState } });
      }
    } catch (saveError: any) {
      setError(saveError.message || 'Failed to publish data point(s).');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 24, color: '#6b7280', textAlign: 'center' }}>Loading...</div>;

  return (
    <div className="sge-page">
      {!embedded && <PageBackNavigation onBack={handleBack} label={returnLabel} />}
      {!embedded && <DataEntryDraftList onResume={resumeDraft} refreshKey={draftRevision} disabled={busy}
        onDeleted={id => {
          if (draftId === id) {
            setDraftId(null);
            setMessage('Saved draft deleted. Your current form entries have not been cleared.');
          }
        }} />}
      {message && <p role="status">{message}</p>}
      <fieldset className="sge-form-fields" disabled={busy}>
      {error && (
        <div role="alert" style={{ marginBottom: 16, padding: '12px 14px', borderRadius: 8, background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c' }}>
          {error}
        </div>
      )}
      {entryHeading && <div className="sge-breadcrumb">{entryHeading}</div>}

      {studentLocked ? (
        <div className="sge-info-card">
          <strong>Student: {students[0]?.full_name || lockedStudent.full_name || (students[0] ? `${students[0].first_name || ''} ${students[0].last_name || ''}`.trim() : '') || `Student ${lockedStudentId}`}</strong>
          <div className="sge-selected-student-count" role="status">1 student selected</div>
          {!students.length && <div role="alert">This student is not in your accessible class roster. Data cannot be published.</div>}
        </div>
      ) : <div className="sge-info-card">
        <div className="sge-class-row">
          <label className="sge-class-label" htmlFor="class-select">Class:</label>
          <select
            id="class-select"
            className="sge-class-select"
            value={classId}
            onChange={(e) => {
              setClassId(e.target.value);
              setSelectedGroupId('');
              setSelectedStudents([]);
              setStudentDrafts({});
              setStudentSearchTerm('');
            }}
            disabled={classOptions.length === 0}
          >
            {classOptions.map((cls) => (
              <option key={cls.class_id} value={cls.class_id}>
                {cls.class_name || `Class ${cls.class_id}`}
              </option>
            ))}
          </select>
        </div>

        <div className="sge-class-row">
          <label className="sge-class-label" htmlFor="group-select">Group:</label>
          <select
            id="group-select"
            className="sge-class-select"
            value={selectedGroupId}
            onChange={(e) => {
              setSelectedGroupId(e.target.value);
              setSelectedStudents([]);
              setStudentDrafts({});
              setStudentSearchTerm('');
            }}
            disabled={!classId || loadingGroups || classGroups.length === 0}
          >
            <option value="">All class students</option>
            {classGroups.map((entry) => (
              <option key={entry.breakout_room_id} value={String(entry.breakout_room_id)}>
                {entry.name || `Group ${entry.breakout_room_id}`} ({Array.isArray(entry.students) ? entry.students.length : 0})
              </option>
            ))}
          </select>
        </div>

        {classId && !loadingGroups && classGroups.length === 0 && (
          <div style={{ fontSize: '0.78rem', color: '#7a9cbf', marginBottom: 8 }}>
            No groups are assigned to this class yet.
          </div>
        )}
        {selectedGroup && (
          <div style={{ fontSize: '0.78rem', color: '#1e3a5f', fontWeight: 700, marginBottom: 8 }}>
            Showing students in {selectedGroup.name || 'selected group'}.
          </div>
        )}

        <div className="sge-students-label">STUDENT(S):</div>
        <input
          className="sge-input-pill"
          type="text"
          placeholder="Search students..."
          value={studentSearchTerm}
          onChange={(e) => setStudentSearchTerm(e.target.value)}
          style={{ marginBottom: 10 }}
        />
        <StudentNameOrderToggle style={{ marginBottom: 10 }} />
        <label className="sge-all-toggle">
          <input type="checkbox" checked={allStudentsSelected} onChange={toggleAllStudents} />
          all shown students
        </label>
        <div style={{ fontSize: '0.78rem', color: '#7a9cbf', marginBottom: 8 }}>
          Showing {filteredStudents.length} of {students.length} students
        </div>
        <div className="sge-selected-student-count" role="status">
          {selectedStudents.length} student{selectedStudents.length === 1 ? '' : 's'} selected
        </div>

        <div className="sge-student-grid">
          {filteredStudents.map((student) => (
            <label key={student.student_id} className="sge-student-label">
              <input
                type="checkbox"
                checked={selectedStudents.includes(student.student_id)}
                onChange={() => toggleStudent(student.student_id)}
              />
              {student.full_name || `${student.first_name || ''} ${student.last_name || ''}`.trim() || `Student ${student.student_id}`}
            </label>
          ))}
          {filteredStudents.length === 0 && (
            <div style={{ color: '#8faac5', fontSize: '0.85rem' }}>No students match your search.</div>
          )}
        </div>
      </div>}

      <div className="sge-divider" />

      <div className="sge-entry-grid">
        <div className="sge-standards-panel">
          <div className="sge-standard-label">UPDATING STANDARDS</div>
          <div className="sge-standard-text sge-standard-text-block">
            {selectedStandardsDetails.length > 0 ? (
              <ul className="sge-standards-list">
                {selectedStandardsDetails.map((standard) => (
                  <li key={standard.key} className="sge-standards-item">
                    <span className="sge-standards-code">{standard.code}</span>
                    {standard.description ? (
                      <span className="sge-standards-description">{standard.description}</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : (
              <span>Enter a standard code below to publish grades.</span>
            )}
          </div>
        </div>

        <div className="sge-title-row">
          <div className="sge-title-input-wrap sge-data-point-title-wrap">
            <label className="sge-title-input-label" htmlFor="data-point-title">Title of Data Point*</label>
            <input
              id="data-point-title"
              className="sge-title-input"
              placeholder="Enter a clear title for this data point"
              value={titleOfDataPoint}
              onChange={(e) => setTitleOfDataPoint(e.target.value)}
            />
          </div>

          {selectionList.length === 0 && (
            <input
              className="sge-input-pill"
              placeholder="Standard code (e.g. 3.AD.2.A.a)"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          )}
        </div>

        <div className="sge-title-col">
          <div className="sge-type-card">
            <div className="sge-type-card-title">Select Type of Data</div>
            <button type="button" className={`sge-type-btn ${dataType === 'observation' ? 'active' : ''}`} onClick={() => setDataType('observation')}>Observation</button>
            <button type="button" className={`sge-type-btn ${dataType === 'assignment' ? 'active' : ''}`} onClick={() => setDataType('assignment')}>Assignment</button>
            <button type="button" className={`sge-type-btn ${dataType === 'assessment' ? 'active' : ''}`} onClick={() => setDataType('assessment')}>Assessment</button>
            {submissionStatusEnabled && (
              <div className="sge-submission-status-section">
                <div className="sge-submission-status-label">Submission Status</div>
                <div className="sge-help-text">Applies only to the selected timeliness standard matching each student's recorded grade:
                  On Time = 95, Late = 70, Incomplete = 45. Other standards keep their entered scores.</div>
                <div className="sge-submission-status-group" role="group" aria-label="Assignment submission status">
                  {SUBMISSION_STATUSES.map((status) => (
                    <button
                      key={status.id}
                      type="button"
                      className={`sge-submission-btn sge-submission-btn-${status.id} ${submissionStatus === status.id ? 'active' : ''}`}
                      onClick={() => setSubmissionStatus(status.id)}
                      aria-pressed={submissionStatus === status.id}
                    >
                      {status.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="sge-assess-row">
              <button type="button" className={`sge-assess-btn ${assessmentType === 'formative' ? 'active' : ''}`} onClick={() => setAssessmentType('formative')}>Formative</button>
              <button type="button" className={`sge-assess-btn ${assessmentType === 'summative' ? 'active' : ''}`} onClick={() => setAssessmentType('summative')}>Summative</button>
            </div>
            <div className="sge-date-field">
              <label className="sge-section-label" htmlFor="data-point-date">Date</label>
              <input id="data-point-date" type="date" className="sge-input-pill" value={dateValue} onChange={(e) => setDateValue(e.target.value)} />
            </div>
          </div>
        </div>

        <div className="sge-metrics-col">
          <div className="sge-score-row">
            <label className="sge-score-label-pill" htmlFor="data-point-score">Score*</label>
            <div className="sge-score-control-group">
              <div className="sge-score-color-row">
                {GRADING_COLORS.map((color) => {
                  return (
                    <button
                      key={color}
                      type="button"
                      className={`sge-score-color-btn sge-score-color-btn-${color} ${displayedScoreColor === color ? 'active' : ''}`}
                      aria-pressed={displayedScoreColor === color}
                      aria-label={`${COLOR_NAMES[color]} score preset`}
                      title={`${COLOR_NAMES[color]}: ${GRADING_STYLES[color].label} (${COLOR_SCORE_PRESETS[color]})`}
                      onClick={() => {
                        setSelectedScoreColor(color);
                        setScore('');
                        setFraction('');
                      }}
                    >
                      <span className="sge-score-color-copy">{GRADING_STYLES[color].label}</span>
                      {showPercentageRanges && <span className="sge-color-percentage-range">{GRADING_PERCENTAGE_RANGES[color]}</span>}
                    </button>
                  );
                })}
              </div>
              <span className="sge-score-or">or</span>
              <div className="sge-score-custom-field">
                <input
                  id="data-point-score"
                  type="number"
                  className="sge-score-custom-input"
                  value={displayedScore}
                  onChange={(e) => {
                    setScore(e.target.value);
                    setSelectedScoreColor('');
                    setFraction('');
                  }}
                />
                <span className="sge-score-percent">%</span>
              </div>
              <FractionScoreInput label="Score fraction" value={fraction} onChange={value => {
                setFraction(value);
                setScore('');
                setSelectedScoreColor('');
              }} />
            </div>
          </div>
          <div className="sge-score-help">
            Pick a colour, enter a percentage, or enter earned/possible points. Fractions calculate a whole-number percentage. {GRADING_COLORS.map((color) => `${COLOR_NAMES[color]} = ${COLOR_SCORE_PRESETS[color]}`).join(', ')}.
          </div>
          <label className="ae-support-checkbox">
            <input type="checkbox" checked={accommodationsApplied} onChange={(event) => setAccommodationsApplied(event.target.checked)} />
            <span>{ADDITIONAL_SUPPORT_LABEL}</span>
          </label>
          <div className="sge-score-help">Additional support beyond what everyone received. Adjust for individual students in Preview.</div>
        </div>

        <section className="sge-notes-evidence-section" aria-labelledby="notes-visibility-heading">
          <h2 id="notes-visibility-heading" className="sge-notes-evidence-heading">Notes &amp; Visibility</h2>
          <div className="sge-notes-evidence-grid">
        <div className="sge-notes-col">
          <label className="sge-section-label" htmlFor="data-point-notes">Notes</label>
          <textarea id="data-point-notes" className="sge-notes-area" placeholder="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
          <div className="sge-visibility-row">
            <button type="button" className={`sge-vis-btn ${visibility === 'private' ? 'active' : ''}`} onClick={() => setVisibility('private')}>Private</button>
            <button type="button" className={`sge-vis-btn ${visibility === 'shared' ? 'active' : ''}`} onClick={() => setVisibility('shared')}>Shared (families)</button>
          </div>
          <div style={{ fontSize: 11, marginTop: 4 }}>Applies to notes and uploaded evidence.</div>
        </div>

        <div className="sge-file-card">
          <div className="sge-file-title">Insert File</div>
          <label htmlFor="evidence-file-input" className="sge-file-icon-wrap" style={{ cursor: 'pointer' }}>
            <div className="sge-file-icon">
              <svg width="24" height="28" viewBox="0 0 24 28" fill="none">
                <path d="M4 2h10l6 6v18a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z" fill="#d6e8f8" stroke="#9eb8d8" strokeWidth="1.5"/>
                <path d="M14 2v6h6" stroke="#9eb8d8" strokeWidth="1.5" fill="none"/>
              </svg>
            </div>
            <span className="sge-file-plus">+</span>
          </label>
          <label className="sge-file-btn-label" htmlFor="evidence-file-input">
            {uploading ? 'Uploading…' : 'Choose File'}
          </label>
          <input id="evidence-file-input" type="file" className="sge-file-input" onChange={(e) => handleEvidenceUpload(e.target.files?.[0])} />
          {evidenceFilename && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <div style={{ fontSize: '0.78rem', color: '#2e7d32', textAlign: 'center' }}>✓ Evidence attached</div>
              <button
                type="button"
                onClick={handleViewEvidence}
                disabled={viewingEvidence}
                className="sge-file-btn-label"
                style={{ cursor: viewingEvidence ? 'default' : 'pointer' }}
              >
                {viewingEvidence ? 'Opening…' : 'View File'}
              </button>
            </div>
          )}
        </div>
          </div>
        </section>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%', marginTop: '8px' }}>
        <div className="sge-selected-student-count">
          Entering data for {selectedStudents.length} selected student{selectedStudents.length === 1 ? '' : 's'}.
        </div>
        <button
          className="sge-btn sge-btn-preview"
          onClick={() => setShowPreview((prev) => !prev)}
          disabled={selectedStudents.length === 0}
        >
          {showPreview ? 'Hide Preview' : 'Preview Data Point(s)'}
        </button>
        <button type="button" className="sge-btn sge-btn-preview" onClick={handleSaveDraft}
          disabled={busy || loading || !selectedStudents.length || (studentLocked && !students.length)}>
          {savingDraft ? 'Saving Draft...' : 'Save Draft'}
        </button>
        <button
          className="sge-btn sge-btn-publish"
          onClick={handleSave}
          disabled={busy || loading || (studentLocked && !students.length)}
        >
          {saving ? 'Saving…' : 'Publish Data Point(s)'}
        </button>
      </div>

      {/* ── Preview panel ── */}
      {showPreview && (
        <div className="sge-preview-panel">
          <div className="sge-preview-title">Preview & Edit by Student</div>
          {previewRows.length === 0 ? (
            <div style={{ color: '#888', fontSize: '0.9rem' }}>No students selected.</div>
          ) : (
            <div className="sge-preview-students">
              {previewRows.map((row) => (
                <section key={row.studentId} className="sge-preview-student-card" aria-label={`${row.studentLabel} data point preview`}>
                  <h3 className="sge-preview-student">{row.studentLabel}</h3>
                  <div className="sge-preview-grading">
                    <div className="sge-preview-standards">
                      {row.cells.map((cell) => (
                        <div
                          key={`${row.studentId}-${cell.standardCode}`}
                          className={`sge-preview-cell ${cell.color ? `sge-preview-row-${cell.color}` : 'sge-preview-row-unrated'}${row.accommodationsApplied ? ' ae-supported-data-point' : ''}`}
                          role="group"
                          aria-label={`${row.studentLabel} ${cell.standardCode} grade`}
                        >
                          <h4 className={compactPreviewColors && cell.standardDescription ? 'sge-preview-head-description' : 'sge-preview-head-code'}>
                            {compactPreviewColors ? cell.standardDescription || cell.standardCode : cell.standardCode}
                          </h4>
                          <div className={`sge-preview-color-row${compactPreviewColors ? ' sge-preview-color-row-compact' : ''}`}>
                            {GRADING_COLORS.map((color) => (
                              <button
                                key={`${row.studentId}-${cell.standardCode}-${color}`}
                                type="button"
                                disabled={cell.submissionScored}
                                className={`sge-preview-color-btn sge-preview-color-btn-${color}${cell.selectedScoreColor === color ? ' active' : ''}`}
                                onClick={() => updateCellDraft(row.studentId, cell.standardCode, { selectedScoreColor: color, score: '', fraction: '' })}
                                aria-label={`${row.studentLabel} ${cell.standardCode} ${color} score preset`}
                                aria-pressed={cell.selectedScoreColor === color}
                                title={`${GRADING_STYLES[color].label} (${COLOR_SCORE_PRESETS[color]})`}
                              >
                                {!compactPreviewColors && (
                                  <>
                                    {GRADING_STYLES[color].label}
                                    {showPercentageRanges && <span className="sge-color-percentage-range">{GRADING_PERCENTAGE_RANGES[color]}</span>}
                                  </>
                                )}
                              </button>
                            ))}
                          </div>
                          <div className="sge-preview-score-field">
                            <input
                              type="number"
                              disabled={cell.submissionScored}
                              value={cell.score}
                              onChange={(event) => updateCellDraft(row.studentId, cell.standardCode, { score: event.target.value, selectedScoreColor: '', fraction: '' })}
                              placeholder="Score"
                              aria-label={`${row.studentLabel} ${cell.standardCode} score`}
                            />
                            <span>%</span>
                          </div>
                          {!cell.submissionScored && (
                            <FractionScoreInput label={`${row.studentLabel} ${cell.standardCode} fraction`} value={cell.fraction}
                              onChange={value => updateCellDraft(row.studentId, cell.standardCode, { fraction: value, score: '', selectedScoreColor: '' })} />
                          )}
                          {cell.submissionScored && <div className="sge-help-text">Score set by submission status below.</div>}
                        </div>
                      ))}
                    </div>
                    <label className="sge-preview-support-checkbox">
                        <input type="checkbox" checked={row.accommodationsApplied}
                          aria-label={`${row.studentLabel} accommodations/modifications applied`}
                          onChange={(event) => updateStudentDraft(row.studentId, { accommodationsApplied: event.target.checked })} />
                        <span>{ADDITIONAL_SUPPORT_LABEL}</span>
                    </label>
                    {row.submissionCodes.length > 0 && (
                      <div className="sge-preview-submission-status">
                        <span className="sge-preview-submission-label">Submission:</span>
                        <div className="sge-preview-submission-group" role="group" aria-label={`${row.studentLabel} submission status`}>
                          {SUBMISSION_STATUSES.map((status) => (
                            <button
                              key={status.id}
                              type="button"
                              className={`sge-preview-submission-btn sge-preview-submission-btn-${status.id} ${row.submissionStatus === status.id ? 'active' : ''}`}
                              onClick={() => updateStudentDraft(row.studentId, { submissionStatus: status.id })}
                              aria-pressed={row.submissionStatus === status.id}
                              aria-label={`${row.studentLabel} ${status.label}`}
                            >
                              {status.label}
                            </button>
                          ))}
                        </div>
                        <div className="sge-help-text">
                          Submission scoring applies to: {row.submissionCodes.join(', ')}
                        </div>
                      </div>
                    )}
                  </div>
                  <PreviewNotes studentLabel={row.studentLabel} notes={row.notes}
                    onChange={(event) => updateStudentDraft(row.studentId, { notes: event.target.value })} />
                </section>
              ))}
            </div>
          )}
        </div>
      )}

      </fieldset>
      {error && <div className="sge-error">{error}</div>}
      {!embedded && <PageBackNavigation onBack={handleBack} label={returnLabel} position="bottom" />}
      {!embedded && <BackToTop />}
    </div>
  );
}

export default StandardGradeEntry;
